import React from "react";
import { useTranslation } from "react-i18next";

import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import { formatNotificationTimestamp } from "../../utils/notificationTime";
import { getNotificationIcon } from "./notificationMeta";

/**
 * One row in the notifications inbox.
 *
 * A row, not a card — a list of bordered cards inside an already-bordered panel
 * is nested-card territory and reads as clutter at this density.
 *
 * Not interactive, and that is on purpose. `/mine` carries no deep link, and
 * with no read state to record there is nothing for a tap to do; a control that
 * does nothing when pressed is worse than plain text, and a screen reader would
 * still announce it as a button.
 */
const NotificationItem = ({ notification }) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();

  // `Type` still picks the row's icon, but it is never shown as text: it is an
  // internal category name the backend invents at will ("PolicyRenewal"), with
  // no Arabic form and no meaning to a customer.
  const Icon = getNotificationIcon(notification?.Type);
  const title = translate(notification?.Title, notification?.Title_ar);
  const subtitle = translate(notification?.Subtitle, notification?.Subtitle_ar);
  const message = translate(notification?.Message, notification?.Message_ar);
  const timestamp = formatNotificationTimestamp(
    notification?.DateCreated,
    translate,
    i18n.resolvedLanguage
  );

  return (
    <li className="flex w-full gap-3 px-4 py-3.5 text-start">
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon className="size-[18px]" aria-hidden="true" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-semibold leading-5 text-foreground">
          {title}
        </p>

        {subtitle && (
          <p className="truncate text-xs font-medium leading-4 text-muted-foreground">
            {subtitle}
          </p>
        )}

        {message && (
          <p className="text-[13px] leading-5 text-muted-foreground">
            {message}
          </p>
        )}

        {/* Hero image is optional and CDN-hosted; a dead link must not leave a
            gap in the row, so it removes itself on error. */}
        {notification?.Image && (
          <img
            src={notification.Image}
            alt=""
            loading="lazy"
            className="mt-1.5 h-28 w-full rounded-md object-cover"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        )}

        <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
          {timestamp}
        </p>
      </div>
    </li>
  );
};

export default NotificationItem;
